import { Spin } from 'antd';
import { Link, useParams } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import { StatusDot } from '@/components/StatusDot';
import { templateStateWord } from '@/lib/statusPresentation';
import { templateDecisionHref } from '@/lib/templateLinks';
import { PANE_COPY } from '../copy';
import { scheduleWords } from '../Decisions/choiceWords';
import { useTemplateList, useTemplateSet } from '../templateQueries';
import { ForceMatrix } from './ForceMatrix';
import { openCodesIn, sumForce } from './paneData';
import { usePaneHeadingRef } from './paneFocus';
import { partHeadingOf, sectionCodeOf } from './templateTree';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §2] ONE SECTION — the review site's `subDetail`: what the booklet printed with it, its days
 *  and times, its templates and what each still needs, and its force.
 *
 *  [amended TEMPLATE-FIND] Each template's number leads its row, muted and OUTSIDE the title's link, so
 *  the link's name stays the title and a number read off the Decisions page can be found here too.
 *
 *  ⚠️ IT SHOWS NO COMMAND, INFORMATION OR INSTRUCTIONS OF ITS OWN, AND SAYS WHY. The import folds a
 *  section's into each of its templates (`templateImport.plan.ts`), so the section stores only its
 *  printed lines; each template's pane carries the rest. Saying so is cheaper than a reader looking
 *  for text that was never meant to be here.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const SectionDetail = (): React.ReactElement => {
  const { setKey = '', sectionKey = '' } = useParams();
  const set = useTemplateSet(setKey, { pane: true });
  const list = useTemplateList(setKey, { pane: true });
  const headingRef = usePaneHeadingRef();

  if (set.isPending || list.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }
  const sections = set.data?.sections ?? [];
  const section = sections.find((entry) => entry.key === sectionKey);
  if (!section) return <EmptyState title={PANE_COPY.noSection} />;
  const items = (list.data?.items ?? [])
    .filter((item) => item.sectionKey === sectionKey)
    .sort((a, b) => a.ordinal - b.ordinal);
  const buildable = items.filter((item) => item.readiness.state === 'READY' || item.readiness.state === 'BLOCKED');
  const decided = buildable.find((item) => item.scheduleDecided);
  const days = set.data?.days ?? [];
  const dayName = new Map(days.map((day) => [day.key, day.name] as const));

  return (
    <article className="flex flex-col gap-5" data-testid="section-view">
      <header className="flex flex-col gap-1">
        <Link to={`/templates/${setKey}/part/${encodeURIComponent(section.part)}`} className="text-sm text-link">
          {partHeadingOf(section.part, sections)}
        </Link>
        <h2 key={sectionKey} ref={headingRef} tabIndex={-1} className="text-lg font-semibold text-heading outline-none">
          {`${sectionCodeOf(section.key)}: ${section.title}`}
        </h2>
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground" data-testid="section-meta">
          {section.pages.length > 0 ? <span>{PANE_COPY.pages(section.pages)}</span> : null}
          <span>{PANE_COPY.templates(items.length)}</span>
          <span>{PANE_COPY.dutyPoints(items.reduce((sum, item) => sum + item.dutyPointCount, 0))}</span>
        </p>
      </header>

      {section.sourceText.length > 0 ? (
        <section className="flex flex-col gap-1 text-sm" data-testid="section-printed">
          <h3 className="font-medium text-heading">{PANE_COPY.printed}</h3>
          {section.sourceText.map((line, index) => (
            <p key={`printed-${String(index)}`} className="whitespace-pre-wrap">
              {line}
            </p>
          ))}
        </section>
      ) : null}

      <section className="flex flex-col gap-1 text-sm" data-testid="section-schedule">
        <h3 className="font-medium text-heading">{PANE_COPY.scheduleTitle}</h3>
        {buildable.length === 0 ? (
          <p className="text-muted-foreground">{PANE_COPY.scheduleNotAsked}</p>
        ) : decided ? (
          <p>
            {PANE_COPY.scheduleDecided(decided.dayKeys.map((key) => dayName.get(key) ?? key).join(', '))}{' '}
            <Link to={templateDecisionHref(setKey, 'SCHEDULE', section.key)} className="text-link">
              {PANE_COPY.change}
            </Link>
          </p>
        ) : (
          <p>
            {section.proposal
              ? PANE_COPY.scheduleProposed(scheduleWords(section.proposal, days))
              : PANE_COPY.scheduleNone}{' '}
            <Link to={templateDecisionHref(setKey, 'SCHEDULE', section.key)} className="text-link">
              {PANE_COPY.decide}
            </Link>
          </p>
        )}
      </section>

      {items.length > 0 ? (
        <section className="flex flex-col gap-2" data-testid="section-templates">
          <h3 className="font-medium text-heading">{PANE_COPY.templatesTitle}</h3>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs text-muted-foreground">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left font-medium">
                    {PANE_COPY.template}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium">
                    {PANE_COPY.division}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    {PANE_COPY.dutyPointsColumn}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    {PANE_COPY.pagesColumn}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium">
                    {PANE_COPY.state}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left font-medium">
                    {PANE_COPY.toDecide}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const word = templateStateWord(item);
                  return (
                    <tr key={item.id} className="border-t border-border" data-key={item.key}>
                      <td className="px-3 py-1.5">
                        <span
                          className="mr-2 text-xs tabular-nums text-muted-foreground"
                          data-testid="section-template-number"
                        >
                          {item.key}
                        </span>
                        <Link to={`/templates/${setKey}/t/${item.id}`} className="text-link">
                          {item.title}
                        </Link>
                      </td>
                      <td className="px-3 py-1.5">{item.divisionName ?? item.divisionCode ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{item.dutyPointCount}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {PANE_COPY.pages(item.pages).replace(/^Pages? /, '')}
                      </td>
                      <td className="px-3 py-1.5">
                        <StatusDot tone={word.tone} label={word.label} />
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground">
                        {PANE_COPY.toDecideCount(item.waitsOn.length)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">{PANE_COPY.foldedIn}</p>
        </section>
      ) : (
        <p className="text-sm text-muted-foreground">{PANE_COPY.noTables}</p>
      )}

      {buildable.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="font-medium text-heading">{PANE_COPY.forceSection}</h3>
          <ForceMatrix
            totals={sumForce(items)}
            openColumns={openCodesIn(items, 'FORCE_COLUMN')}
            openSources={openCodesIn(items, 'SOURCE')}
            caption={PANE_COPY.forceCaption}
            testId="section-force"
          />
        </section>
      ) : null}
    </article>
  );
};

export default SectionDetail;
